/* deal-detail.jsx — Premium Deal/Opportunity Detail for Nexus CRM
   Overrides OverviewTab for Deal records only.
   Loaded after overlays.jsx — all globals accessible. */
import { ConfirmDialog } from '../../shared/ui/index.jsx';
const { useState: useStateD, useEffect: useEffectD, useMemo: useMemoD } = React;

/* ══════════════════════════════════════════════════════════════
   STAGE PROGRESSION BAR
   ══════════════════════════════════════════════════════════════ */
const DealStageBar = ({ stageId, stages, onChangeStage, canMove }) => {
  const activeIdx = stages.findIndex(s => s.id === stageId);
  return (
    <div className="nx-deal-stagebar">
      {stages.map((s, i) => {
        const isPast   = i < activeIdx;
        const isActive = i === activeIdx;
        const isWon    = s.kind === 'WON';
        const isLost   = s.kind === 'LOST';
        if (isLost) return null;
        return (
          <button
            type="button"
            key={s.id}
            className={'nx-dsbar-step' + (isActive ? ' is-active' : '') + (isPast ? ' is-past' : '') + (isWon ? ' is-won' : '') + (canMove === false ? ' is-readonly' : '')}
            style={{ '--stage-tone': s.tone }}
            onClick={() => canMove !== false && onChangeStage(s.id)}
            title={canMove === false ? 'No permission to move stage' : 'Move to ' + s.name}
            aria-disabled={canMove === false}
          >
            <span className="nx-dsbar-dot" />
            <span className="nx-dsbar-label">{s.name}</span>
          </button>
        );
      })}
    </div>
  );
};
window.DealStageBar = DealStageBar;


/* ══════════════════════════════════════════════════════════════
   DEAL ACTIVITY SECTION — extracted sub-component
   ══════════════════════════════════════════════════════════════ */
const _icNames = { whatsapp:'MessageCircle', deal:'Target', email:'Mail', note:'StickyNote', call:'Phone', meeting:'Calendar' };
const _icFgs   = { whatsapp:'#0E8C5A', deal:'var(--nx-accent)', email:'#6E3FBA', note:'#C26A1F', call:'#0E8C5A' };

const DealActivitySection = ({ activities, error, onRetry, storeActivity, recordId }) => {
  const items = (activities.length ? activities : storeActivity.filter(a => a.dealId === recordId)).slice(0, 4);
  if (error) {
    return (
      <div className="nx-empty" style={{ padding: '8px 0', justifyContent: 'flex-start', gap: 6 }}>
        <NexusIcon name="AlertCircle" size={13} style={{ color: 'var(--nx-danger, #D93025)', flexShrink: 0 }} />
        <span className="nx-muted nx-mini">Could not load activity. <button type="button" className="nx-link" onClick={onRetry}>Retry</button></span>
      </div>
    );
  }
  if (typeof UniversalTimeline !== 'undefined') {
    return <UniversalTimeline items={items} compact />;
  }
  return (
    <ol className="nx-timeline">
      {items.map(a => (
        <li key={a.id} className="nx-timeline-item">
          <span className="nx-timeline-dot" style={{ background: _icFgs[a.type] || 'var(--nx-text-muted)' }}>
            <NexusIcon name={_icNames[a.type] || 'Activity'} size={11} />
          </span>
          <div className="nx-timeline-body">
            <span className="nx-timeline-text"><strong>{a.who}</strong> {a.what}</span>
            {a.detail && <span className="nx-timeline-detail">{a.detail}</span>}
            <span className="nx-timeline-time">{a.time}</span>
          </div>
        </li>
      ))}
    </ol>
  );
};

/* ══════════════════════════════════════════════════════════════
   HELPERS — module-level to keep DealOverviewTab complexity low
   ══════════════════════════════════════════════════════════════ */

const _taskTone = { High: 'red', Urgent: 'red', Medium: 'amber' };
const fmtCloseDate = (v) => v ? new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

const getDealDisplayProps = (stageKind, stageTone, stageName, stageProb, priority) => {
  let prob         = stageProb ?? 0;
  let stageBg      = stageTone || 'var(--nx-accent)';
  let probLabel    = '';
  let kpiColor     = 'var(--nx-text)';
  let kpiStageLabel = stageName || '—';
  let snapshotTone = 'blue';
  if (stageKind === 'WON') {
    prob = 100; stageBg = 'var(--nx-success)'; kpiColor = 'var(--nx-success)';
    kpiStageLabel = 'Won'; snapshotTone = 'green'; probLabel = '✓ Won';
  } else if (stageKind === 'LOST') {
    prob = 0; stageBg = 'var(--nx-danger)'; kpiColor = 'var(--nx-danger)';
    kpiStageLabel = 'Lost'; snapshotTone = 'red'; probLabel = '✗ Lost';
  } else {
    probLabel = prob + '% probability';
  }
  let priorityTone = 'gray';
  if (priority === 'High') { priorityTone = 'red'; }
  else if (priority === 'Medium') { priorityTone = 'amber'; }
  return { prob, stageBg, probLabel, kpiColor, kpiStageLabel, snapshotTone, priorityTone };
};

const useDealActivity = (recordId) => {
  const [activities, setActivities] = useStateD([]);
  const [error, setError] = useStateD(false);
  useEffectD(() => {
    setError(false);
    const wsId = window.__nx_auth?.user?.activeWorkspaceId;
    if (!wsId || !recordId || recordId.includes('_')) return;
    let cancelled = false;
    window.__nx_api.activity.listForDeal(wsId, recordId, { pageSize: 5 })
      .then(res => {
        if (cancelled) return;
        const rows = Array.isArray(res) ? res : (res?.data ?? []);
        setActivities(rows.map(window.__nx_api.activity.normActivity));
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [recordId]);
  const retry = () => { setError(false); setActivities([]); };
  return { activities, error, retry };
};

const makeHandleStageChange = (stages, update, setPendingWon) => stageId => {
  const s = stages.find(x => x.id === stageId);
  if (s?.kind === 'WON') { setPendingWon(stageId); }
  else { update('stageId', stageId); }
};

/* ══════════════════════════════════════════════════════════════
   DEAL OVERVIEW TAB
   ══════════════════════════════════════════════════════════════ */
const DealOverviewTab = ({ record, type, setTab }) => {
  const store  = useStore();

  // Always use the deal's own pipeline stages, not the active pipeline.
  // Pipeline is immutable on existing deals (backend enforces STAGE_PIPELINE_MISMATCH).
  const dealPipelineId = record.pipelineId;
  const stages = dealPipelineId
    ? (store.allStages || [])
        .filter(s => s.pipelineId === dealPipelineId)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        .map(s => ({ id: s.id, uuid: s.id, name: s.name, tone: s.color || s.tone || '#94a3b8', prob: s.probability ?? 0, kind: s.kind || 'OPEN', pipelineId: s.pipelineId }))
    : store.pipeline || [];

  // record.stageId may be a short ID (active pipeline) or UUID (non-active pipeline);
  // resolve to UUID for matching against allStages-derived stage list.
  const effectiveStageId = dealPipelineId
    ? ((store.pipeline || []).find(s => s.id === record.stageId)?.uuid || record.stageId)
    : record.stageId;

  const update = (field, value) => store.update(type, record.id, { [field]: value });
  const canMoveStage = (window.__nx_rbac?.can('crm.deals.stage') || window.__nx_rbac?.can('crm.manage')) ?? true;

  const [pendingWonStageId, setPendingWonStageId] = useStateD(null);
  const { activities: dealActivities, error: activityError, retry: retryActivity } = useDealActivity(record.id);

  const handleStageChange = makeHandleStageChange(stages, update, setPendingWonStageId);

  const stage    = stages.find(s => s.id === effectiveStageId);
  const pipeline = (store.pipelines || []).find(p => p.id === dealPipelineId || p.id === stage?.pipelineId);
  const company  = store.companies.find(c => c.id === record.companyId);
  const contact = store.people.find(p => p.id === record.contactId);
  const fmt     = n => '$' + (n >= 1000 ? (n / 1000).toFixed(1) + 'K' : n);

  const { prob, stageBg, probLabel, kpiColor, kpiStageLabel, snapshotTone, priorityTone } =
    getDealDisplayProps(stage?.kind, stage?.tone, stage?.name, stage?.prob, record.priority);

  const openTasks = store.tasks
    .filter(t => !t.done && t.status !== 'cancelled' && t.dealId === record.id)
    .slice(0, 4);

  return (
    <div className="nx-drawer-overview" style={{ padding: 0 }}>

      {pendingWonStageId && (
        <ConfirmDialog
          title="Mark Deal as Won?"
          desc="This will close the deal as Won. You can reopen it later from the archive tab."
          confirmLabel="Mark Won"
          onConfirm={() => { update('stageId', pendingWonStageId); setPendingWonStageId(null); }}
          onCancel={() => setPendingWonStageId(null)}
        />
      )}

      {/* Stage progression bar */}
      <div className="nx-deal-stage-section">
        <DealStageBar stageId={effectiveStageId} stages={stages} onChangeStage={handleStageChange} canMove={canMoveStage} />
        <div className="nx-deal-prob-row">
          <div className="nx-deal-prob-bar">
            <div className="nx-deal-prob-fill" style={{ width: prob + '%', background: stageBg }} />
          </div>
          <span className="nx-deal-prob-pct">{probLabel}</span>
        </div>
      </div>

      <div className="nx-pov-layout">

        {/* ── MAIN column ── */}
        <div className="nx-pov-main">

          {/* Deal KPI strip */}
          <div className="nx-co-kpi-strip">
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val">{fmt(record.value)}</span>
              <span className="nx-co-kpi-lbl">Deal Value</span>
            </div>
            <div className="nx-co-kpi-sep" />
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val" style={{ color: kpiColor }}>{kpiStageLabel}</span>
              <span className="nx-co-kpi-lbl">Stage</span>
            </div>
            <div className="nx-co-kpi-sep" />
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val">{record.age != null ? <>{record.age}<span style={{ fontSize: 11, fontWeight: 400, color: 'var(--nx-text-muted)' }}>d</span></> : '—'}</span>
              <span className="nx-co-kpi-lbl">Age</span>
            </div>
            <div className="nx-co-kpi-sep" />
            <div className="nx-co-kpi">
              <span className="nx-co-kpi-val">{fmtCloseDate(record.closeDate)}</span>
              <span className="nx-co-kpi-lbl">Close Date</span>
            </div>
          </div>

          {/* Deal Information */}
          <DrawerPanel title="Deal Information">
            <div className="nx-prop-grid">
              <EditableProp label="Title"      value={record.title}       onSave={v => update('title', v)} />
              <EditableProp label="Value"      value={record.value} mono prefix="$" numeric onSave={v => update('value', Number(v) || 0)} />
              <EditableProp label="Close Date" value={record.closeDate} date mono onSave={v => update('closeDate', v)} />
              <SelectProp label="Priority" value={record.priority}
                options={['High', 'Medium', 'Low']}
                onSave={v => update('priority', v)}
                pill={priorityTone} />
              {pipeline && <Prop label="Pipeline" value={pipeline.name} />}
              <SelectProp label="Stage" value={stage?.name}
                options={stages.map(s => ({ value: s.id, label: s.name }))}
                onSave={v => update('stageId', v)} />
              <Prop label="Probability" value={prob + '%'} mono />
            </div>
          </DrawerPanel>

          {/* Related Records */}
          <DrawerPanel title="Related Records">
            <div className="nx-prelated-section">
              <div className="nx-prelated-label">Company</div>
              {company
                ? (
                  <button type="button" className="nx-prelated-row" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(company); }}>
                    <span className="nx-prelated-icon" style={{ background: 'var(--nx-accent-tint)', color: 'var(--nx-accent)' }}><NexusIcon name="Building2" size={12} /></span>
                    <div className="nx-prelated-body">
                      <span className="nx-prelated-title">{company.name}</span>
                      {(company.industry || company.location) && (
                        <span className="nx-prelated-sub">{[company.industry, company.location].filter(Boolean).join(' · ')}</span>
                      )}
                    </div>
                    <NexusIcon name="ArrowUpRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                  </button>
                )
                : <div className="nx-muted nx-mini" style={{ padding: '4px 0', display: 'block' }}>No company linked.</div>
              }
            </div>
            <div className="nx-prelated-section">
              <div className="nx-prelated-label">Primary Contact</div>
              {contact
                ? (
                  <button type="button" className="nx-prelated-row" onClick={() => { window.__nx_drawer_back = record; store.openDrawer(contact); }}>
                    <Avatar name={contact.name} tone="var(--color-primary-500)" size={26} style={{ flexShrink: 0 }} />
                    <div className="nx-prelated-body">
                      <span className="nx-prelated-title">{contact.name}</span>
                      <span className="nx-prelated-sub">{contact.role}{contact.company ? ' · ' + contact.company : ''}</span>
                    </div>
                    <NexusIcon name="ArrowUpRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
                  </button>
                )
                : <div className="nx-muted nx-mini" style={{ padding: '4px 0', display: 'block' }}>No contact linked.</div>
              }
            </div>
          </DrawerPanel>

          {/* Stage History */}
          <DrawerPanel title="Stage History">
            {typeof StageHistoryPanel !== 'undefined'
              ? <StageHistoryPanel record={record} stages={stages} />
              : <span className="nx-muted nx-mini" style={{ padding: 8, display: 'block' }}>Stage history unavailable.</span>
            }
          </DrawerPanel>

          {/* Recent Activity */}
          <DrawerPanel title="Recent Activity"
            actions={<button type="button" className="nx-panel-link" onClick={() => setTab?.('activity')}>See All <NexusIcon name="ArrowRight" size={11} /></button>}>
            <DealActivitySection
              activities={dealActivities}
              error={activityError}
              onRetry={retryActivity}
              storeActivity={store.activity}
              recordId={record.id}
            />
          </DrawerPanel>

          {/* Custom Fields — CF-UI-002: Deal Overview integration */}
          <DrawerPanel title="Custom Fields">
            {window.CustomFieldsPanel
              ? React.createElement(window.CustomFieldsPanel, { record, entityType: 'deals' })
              : <span className="nx-muted nx-mini" style={{ padding: '8px 0', display: 'block' }}>Custom fields not available.</span>
            }
          </DrawerPanel>

        </div>

        {/* ── SIDE column ── */}
        <div className="nx-pov-side">

          {typeof TagEditor !== 'undefined' && (
            <DrawerPanel title="Tags">
              <div className="nx-prop-grid"><TagEditor record={record} type="deals" /></div>
            </DrawerPanel>
          )}

          {/* AI_DISABLED — re-enable after Enterprise AI Platform launch:
          {typeof AIEntityCard !== 'undefined' && (
            <AIEntityCard record={record} entityType="deals" />
          )} */}

          {typeof ForecastPanel !== 'undefined' && (
            <DrawerPanel title="Revenue Forecast">
              <ForecastPanel record={record} stage={stage} />
            </DrawerPanel>
          )}

          {/* CRM Snapshot */}
          <DrawerPanel title="CRM Snapshot">
            <div className="nx-crm-snapshot">
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Stage</span>
                <Pill tone={snapshotTone}>{stage?.name || '—'}</Pill>
              </div>
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Priority</span>
                <Pill tone={priorityTone}>{record.priority}</Pill>
              </div>
              <div className="nx-crm-row">
                <span className="nx-crm-lbl">Owner</span>
                <SelectProp label="" value={record.owner}
                  options={store.memberNames?.length ? store.memberNames : [window.__nx_auth?.user?.name].filter(Boolean)}
                  onSave={v => update('owner', v)} avatar />
              </div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Value</span><span className="nx-crm-val" style={{ fontWeight: 800, color: 'var(--nx-accent)' }}>{fmt(record.value)}</span></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Close Date</span><span className="nx-crm-val">{fmtCloseDate(record.closeDate)}</span></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Age</span><span className="nx-crm-val">{record.age != null ? record.age + 'd' : '—'}</span></div>
              <div className="nx-crm-row"><span className="nx-crm-lbl">Last Touch</span><span className="nx-crm-val">{record.lastTouch || '—'}</span></div>
              {record.alerts?.length > 0 && (
                <div className="nx-crm-row">
                  <span className="nx-crm-lbl">Alerts</span>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {typeof DealAlertChips !== 'undefined' && <DealAlertChips alerts={record.alerts} size="xs" />}
                  </div>
                </div>
              )}
            </div>
          </DrawerPanel>

          {/* Open Tasks */}
          <DrawerPanel title="Open Tasks"
            actions={<button type="button" className="nx-panel-link" onClick={() => store.openCreateForm('tasks', { dealId: record.id, personId: record.contactId || null, companyId: record.companyId || null, assignee: record.owner || '', _context: { source: 'deal', readOnly: ['dealId', 'personId', 'companyId', 'assignee'] } })}><NexusIcon name="Plus" size={10} /> Add</button>}>
            {openTasks.map(t => (
              <div key={t.id} className={'nx-mini-task' + (t.status === 'in_progress' ? ' is-in-progress' : '')}>
                <button type="button" className="nx-check" onClick={() => store.update('tasks', t.id, { done: !t.done })}>
                  {t.done && <NexusIcon name="Check" size={10} />}
                </button>
                <button type="button" className="nx-task-title" style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', background:'none', border:'none', padding:0, textAlign:'left', cursor:'pointer', color:'inherit', font:'inherit' }} onClick={() => store.openDrawer(t)}>{t.title}</button>
                {t.status === 'in_progress' && <Pill tone="blue">In Progress</Pill>}
                <Pill tone={_taskTone[t.priority] || 'gray'}>{t.priority}</Pill>
              </div>
            ))}
            {openTasks.length === 0 && <div className="nx-muted nx-mini" style={{ padding: '4px 0', display: 'block' }}>No tasks linked to this deal.</div>}
          </DrawerPanel>

          {/* Record Metadata */}
          <DrawerPanel title="Record Metadata">
            <Prop label="Record ID" value={record.id} mono />
            <Prop label="Created"  value={record.createdAt ? new Date(record.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <Prop label="Modified" value={record.updatedAt ? new Date(record.updatedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'} />
            <Prop label="Source"   value={record.source || 'Manual'} />
          </DrawerPanel>

        </div>
      </div>
    </div>
  );
};
window.DealOverviewTab = DealOverviewTab;
